import { useEffect, useState, type CSSProperties } from 'react';
import { ElementDimensions, useWindowDimensions } from '../../hooks/useWindowDimensions';
import { GRID_LAYOUT_FALLBACK_HEIGHT_PX, GRID_LAYOUT_FALLBACK_WIDTH_PX } from '../cpsGridTypes';
import { CpsFieldForView } from '@cs-cms/types';

interface ColumnChanges {
  widths: Record<string, number>;
  order: string[];
}

interface UseCpsGridLayoutParams {
  gridRef: React.RefObject<HTMLDivElement | null>;
  gridDataLength: number;
  selectedIndexId: number | undefined;
  columns?: CpsFieldForView[];
  columnChanges?: ColumnChanges;
  layoutRevision?: string;
}

interface UseCpsGridLayoutResult {
  gridDimensions: ElementDimensions;
  noRecordsOverlayStyle: CSSProperties | undefined;
}

export const useCpsGridLayout = (params: UseCpsGridLayoutParams): UseCpsGridLayoutResult => {
  const { gridRef, gridDataLength, selectedIndexId, columns, columnChanges, layoutRevision } = params;
  const [gridDimensions, setGridDimensions] = useState<ElementDimensions>({ width: 0, height: 0 });
  const [noRecordsOverlayStyle, setNoRecordsOverlayStyle] = useState<CSSProperties | undefined>();
  const windowDimensions = useWindowDimensions();

  useEffect(() => {
    if (!gridRef.current) {
      return;
    }

    const paddingSize = 30;
    const rect = gridRef.current.getBoundingClientRect();
    const rawHeight = document.body.offsetHeight - rect.top - paddingSize - 30;
    const rawWidth = rect.width - paddingSize;
    const height = Number.isFinite(rawHeight) && rawHeight > 0 ? rawHeight : GRID_LAYOUT_FALLBACK_HEIGHT_PX;
    const width = Number.isFinite(rawWidth) && rawWidth > 0 ? rawWidth : GRID_LAYOUT_FALLBACK_WIDTH_PX;

    setGridDimensions({ height, width });
  }, [windowDimensions, selectedIndexId, gridRef, layoutRevision]);

  useEffect(() => {
    if (!gridRef.current) {
      return;
    }

    if (gridDataLength === 0) {
      const tableThead = gridRef.current.getElementsByClassName('k-table-thead')[0];
      const noRecordsWrapper = gridRef.current.getElementsByClassName('k-grid-table-wrap')[0];

      if (tableThead instanceof HTMLElement && noRecordsWrapper instanceof HTMLElement) {
        noRecordsWrapper.style.width = `${tableThead.getBoundingClientRect().width}px`;
      }

      const gridContent = gridRef.current.getElementsByClassName('k-grid-content')[0];
      if (gridContent instanceof HTMLElement) {
        const contentRect = gridContent.getBoundingClientRect();
        const containerRect = gridRef.current.getBoundingClientRect();
        setNoRecordsOverlayStyle({
          top: contentRect.top - containerRect.top,
          left: contentRect.left - containerRect.left,
          width: gridContent.clientWidth,
          height: gridContent.clientHeight
        });
      } else {
        setNoRecordsOverlayStyle(undefined);
      }
    } else {
      setNoRecordsOverlayStyle(undefined);
    }
  }, [windowDimensions, gridDataLength, columns, columnChanges, gridDimensions.height, gridRef, layoutRevision]);

  return { gridDimensions, noRecordsOverlayStyle };
};
